import { AttributeConfigurationDc, AttributeType, DetailedTableInfoDc } from "@evergis/api";

import { useLayerPreview } from "hooks/preview/usePreview";
import { HTTPError } from "ky";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { isLocationMainSelector } from "../../../../ducks/location/selectors";
import { api } from "../../../../evergis/api";
import { createNewAttribute } from "../../../../evergis/core/attributes/createNewAttribute";
import { attributesToTable } from "../../../../evergis/core/import/attributesToTable";
import { SchemaParsed } from "../../../../evergis/core/import/parseSchema";
import { useAddLayersToCurrentProject } from "../../../../hooks/project/useAddLayersToCurrentProject";
import { ResourceMeta } from "../../../../hooks/useResourceMeta";
import { createQueryLayer } from "../../../../resources/layers";
import { HTTPStatus } from "../../../../utils/constants";
import { getIdAttribute } from "../../../../utils/getIdAttribute";
import { getResourceName } from "../../../../utils/resourceName";
import { ImportAttribute, ImportErrorState } from "../types";

interface LayerFromSchemaState {
  loadingLayer: boolean;
  targetLayerName: string;
  targetTableName: string;
  error: ImportErrorState;
}

// eslint-disable-next-line max-lines-per-function
export const useLayerFromSchema = (
  schema: Pick<SchemaParsed, "idAttribute" | "geometryAttribute" | "geometryType">,
  meta: ResourceMeta,
  attributes: ImportAttribute[],
  srid: number,
): [LayerFromSchemaState, VoidFunction] => {
  const { t } = useTranslation("dialogs");
  const [, setPreview] = useLayerPreview();
  const [createQueryLayerService, { data: layerInfo, loading: loadingLayer, error }] = createQueryLayer.use();
  const isProjectLocation = useSelector(isLocationMainSelector);
  const addLayersToCurrentProject = useAddLayersToCurrentProject();
  const { file } = meta.preview;
  const { idAttribute, geometryAttribute, geometryType } = schema;
  const targetLayerName = layerInfo && layerInfo.name;
  const targetTableName = useMemo(() => meta.name || getResourceName(), [meta.name]);
  const [formatError, setFormatError] = useState<ImportErrorState>();
  const [tableError, setTableError] = useState();

  // eslint-disable-next-line max-lines-per-function
  const createLayer = useCallback(async () => {
    const newIdAttribute = getIdAttribute(attributes, idAttribute);

    const table: DetailedTableInfoDc = {
      ...attributesToTable(attributes, newIdAttribute, geometryAttribute, srid),
      name: targetTableName,
      alias: meta.alias,
      description: meta.description,
    };

    setTableError(void 0);

    try {
      const { name: tableName } = await api.tables.create(table);

      await createQueryLayerService({
        input: {
          name: targetTableName,
          alias: meta.alias,
          description: meta.description,
          geometryType: geometryType
            ? ((geometryType.charAt(0).toUpperCase() + geometryType.slice(1)) as unknown as AttributeType)
            : AttributeType.Unknown,
          eql: `SELECT * FROM ${tableName}`,
          srid,
          attributesConfiguration: {
            attributes: attributes.map(
              ({
                type,
                expression,
                stringFormat,
                name,
                alias: attributeAlias,
                sysAttribute,
                columnName,
                ...otherData
              }) =>
                createNewAttribute({
                  type,
                  attributeName: name,
                  alias: attributeAlias,
                  expression,
                  stringFormat,
                  columnName: sysAttribute ? columnName : null,
                  ...otherData,
                }),
            ) as AttributeConfigurationDc[],
            tableName,
            idAttribute: newIdAttribute,
            geometryAttribute,
          },
        },
      });
    } catch (e) {
      setTableError(e);
    }
  }, [
    attributes,
    idAttribute,
    geometryAttribute,
    srid,
    targetTableName,
    meta.alias,
    meta.description,
    createQueryLayerService,
    geometryType,
  ]);

  useEffect(() => {
    if (!targetLayerName) {
      return;
    }

    if (isProjectLocation) {
      addLayersToCurrentProject([layerInfo]);
    }
    if (file) {
      setPreview(targetLayerName, file);
    }
  }, [targetLayerName, isProjectLocation, file]);

  useEffect(() => {
    const errorFinal = error || tableError;

    if (!errorFinal) return;

    (async () => {
      const json = await (errorFinal as HTTPError).response.json();

      if (json?.StatusCode === HTTPStatus.Conflict) {
        setFormatError({ message: t("import.conflictName"), count: 1, log: t("import.conflictName") });
      } else {
        setFormatError({ message: json?.ExceptionMessage, count: 1, log: json?.ExceptionMessage });
      }
    })();
  }, [error, tableError]);

  return [{ loadingLayer, targetLayerName, targetTableName, error: formatError }, createLayer];
};
